import axios from 'axios'
import {canAccessHistoricalHd} from '../../../auth/core/historicalHdAccess'

import * as LandService from '../../Fields/services/LandService'
import {
  getLand,
  getLandAmbient,
  getLandAmbientHd,
  getLandImageAmbient,
  getLandImageAmbientHd,
  listFieldLands,
} from '../../Fields/services/GisDataAdapter'
import {paintAmbientGeojson} from '../../Fields/ViewField/ViewLand/Ambients/ambientMapUtils'
import {
  getAmbientClassCount,
  getAmbientDisplayClassNumber,
} from '../../Fields/ViewField/ViewLand/Ambients/ambientClassPresentation'
import {
  ALTIMETRY_LEGEND_HEIGHT,
  ALTIMETRY_LEGEND_WIDTH,
  buildAltimetryRaster,
  drawAltimetryLegend,
} from '../../Fields/ViewField/ViewLand/Images/Services/Altimetry/Altimetry'
import {
  createSoilMapCanvas,
  getSoilMapLegendModel,
} from '../../Fields/ViewField/ViewLand/Images/Services/SoilMap/soilMapVisuals'
import {buildFieldLandDownloadSubject} from '../../Fields/ViewField/ViewLand/Layers/downloadArchive'
import {
  drawSoilMapLegend,
  getSoilMapLegendHeight,
} from '../../Fields/ViewField/ViewLand/Layers/visualLayerExport'
import {mapState} from '../../Map/MapState'
import * as GeoJsonService from '../../services/GeoJsonService'
import {formatFixedNumber, formatPercent} from '../../../../shared/numberFormat'
import {PLANET_HD_V1_ENABLED} from '../../../../config/featureFlags'
import {
  isPlanetHdProduct,
  normalizeRemoteSensingSource,
} from '../../Fields/ViewField/ViewLand/Images/remoteSensingModel'
import {requestRemoteSensingLayer} from '../../Fields/ViewField/ViewLand/Images/remoteSensingLayerRequest'
import {
  ALL_TECHNICAL_IMAGE_TYPES,
  getStoredTechnicalReportAcquisition,
  getTechnicalReportImageTypes,
  getTechnicalReportProduct,
} from './technicalReportSource'

const IMAGE_TYPES = ALL_TECHNICAL_IMAGE_TYPES
const ENHANCEABLE_IMAGE_TYPES = ['ndvi', 'evi']
const IMAGE_LEGEND_COLORS = ['#1a9641', '#a6d96a', '#fdae61', '#d7191c']
const MAX_EXPORT_WIDTH = 1400
const MAX_EXPORT_HEIGHT = 1700
const GOOGLE_TILE_SERVERS = ['mt0', 'mt1', 'mt2', 'mt3']
const TILE_SIZE = 256
const OVERVIEW_BBOX_PADDING_RATIO = 0.04
const OVERVIEW_CANVAS_FILL_RATIO = 0.98
const OVERVIEW_LABEL_MAX_WIDTH = 160
const STANDARD_FIGURE_MAX_BODY_HEIGHT = 520
const OVERVIEW_FIGURE_MAX_BODY_HEIGHT = 420

const normalizeComparableValue = (value) => {
  if (value === null || value === undefined) {
    return ''
  }

  return String(value).trim()
}

const uniqueCompactValues = (values = []) => {
  const result = []
  const seen = new Set()
  const pushValue = (value) => {
    if (Array.isArray(value)) {
      value.forEach(pushValue)
      return
    }

    const normalized = normalizeComparableValue(value)

    if (!normalized || seen.has(normalized)) {
      return
    }

    seen.add(normalized)
    result.push(normalized)
  }

  values.forEach(pushValue)

  return result
}

const valuesIntersect = (leftValues, rightValues) => {
  const right = new Set(uniqueCompactValues(rightValues))

  return uniqueCompactValues(leftValues).some((value) => right.has(value))
}

const normalizeComparableName = (value) => normalizeComparableValue(value).toLowerCase()

const getLandCandidateIds = (land) =>
  uniqueCompactValues([
    land?.land_id,
    land?.landId,
    land?.id,
    land?.uuid,
    land?.value,
    land?.gis_id,
    land?.resource_id,
    land?.data?.land_id,
    land?.data?.landId,
    land?.data?.id,
    land?.data?.uuid,
    land?.data?.value,
    land?.data?.gis_id,
    land?.data?.resource_id,
    land?.land?.land_id,
    land?.land?.landId,
    land?.land?.id,
    land?.land?.uuid,
    land?.properties?.land_id,
    land?.properties?.landId,
    land?.properties?.id,
    land?.properties?.uuid,
    land?.feature?.properties?.land_id,
    land?.feature?.properties?.landId,
    land?.feature?.properties?.id,
    land?.feature?.properties?.uuid,
  ])

const getLandId = (land) => getLandCandidateIds(land)[0]

const getFieldCandidateIds = (land) =>
  uniqueCompactValues([
    land?.field_id,
    land?.fieldId,
    land?.field?.field_id,
    land?.field?.fieldId,
    land?.field?.id,
    land?.data?.field_id,
    land?.data?.fieldId,
    land?.data?.field?.field_id,
    land?.data?.field?.fieldId,
    land?.data?.field?.id,
    land?.properties?.field_id,
    land?.properties?.fieldId,
    land?.feature?.properties?.field_id,
    land?.feature?.properties?.fieldId,
  ])

const getFieldId = (land) => getFieldCandidateIds(land)[0]

const getLandCandidateNames = (land) =>
  uniqueCompactValues([
    land?.name,
    land?.land_name,
    land?.landName,
    land?.data?.name,
    land?.data?.land_name,
    land?.data?.landName,
    land?.land?.name,
    land?.properties?.name,
    land?.properties?.land_name,
    land?.feature?.properties?.name,
    land?.feature?.properties?.land_name,
  ]).map(normalizeComparableName)

const getFieldCandidateNames = (land) =>
  uniqueCompactValues([
    land?.field_name,
    land?.fieldName,
    land?.field?.name,
    land?.data?.field_name,
    land?.data?.fieldName,
    land?.data?.field?.name,
    land?.properties?.field_name,
    land?.properties?.fieldName,
    land?.feature?.properties?.field_name,
    land?.feature?.properties?.fieldName,
  ]).map(normalizeComparableName)

const getFeatureCandidateIds = (feature) => {
  const properties = feature?.properties || {}

  return uniqueCompactValues([
    properties.land_id,
    properties.landId,
    properties.id,
    properties.uuid,
    properties.value,
    properties.gis_id,
    properties.resource_id,
    properties.resourceId,
    properties.land?.land_id,
    properties.land?.landId,
    properties.land?.id,
    properties.land?.uuid,
    properties.data?.land_id,
    properties.data?.landId,
    properties.data?.id,
    properties.data?.uuid,
    properties.data?.value,
    feature?.id,
  ])
}

const getFeatureFieldCandidateIds = (feature) => {
  const properties = feature?.properties || {}

  return uniqueCompactValues([
    properties.field_id,
    properties.fieldId,
    properties.field?.field_id,
    properties.field?.fieldId,
    properties.field?.id,
    properties.data?.field_id,
    properties.data?.fieldId,
  ])
}

const getFeatureCandidateNames = (feature) => {
  const properties = feature?.properties || {}

  return uniqueCompactValues([
    properties.name,
    properties.land_name,
    properties.landName,
    properties.label,
    properties.land?.name,
    properties.data?.name,
    properties.data?.land_name,
  ]).map(normalizeComparableName)
}

const getFeatureFieldCandidateNames = (feature) => {
  const properties = feature?.properties || {}

  return uniqueCompactValues([
    properties.field_name,
    properties.fieldName,
    properties.field?.name,
    properties.data?.field_name,
    properties.data?.fieldName,
  ]).map(normalizeComparableName)
}

const getFeatureResourceType = (feature) => {
  const properties = feature?.properties || {}

  return normalizeComparableName(
    properties.resource_type || properties.resourceType || properties.type || properties.resource
  )
}

const indexLandByCandidateIds = (index, land) => {
  getLandCandidateIds(land).forEach((landId) => {
    index[landId] = land
  })
}

const findIndexedLandByCandidateIds = (index, candidateIds) => {
  for (const candidateId of uniqueCompactValues(candidateIds)) {
    if (index[candidateId]) {
      return index[candidateId]
    }
  }

  return null
}

const getGeoJsonFeatures = (payload) => {
  const queue = [payload]
  const visited = new Set()

  while (queue.length > 0) {
    const current = queue.shift()

    if (!current || visited.has(current)) {
      continue
    }

    if (typeof current === 'object') {
      visited.add(current)
    }

    if (Array.isArray(current)) {
      if (current.length === 0 || current.some((item) => item?.type === 'Feature' || item?.geometry)) {
        return current
      }

      queue.push(...current)
      continue
    }

    if (Array.isArray(current.features)) {
      return current.features
    }

    queue.push(
      current.data,
      current.geojson,
      current.result,
      current.results,
      current.payload,
      current.collection,
      current.featureCollection
    )
  }

  return []
}

const getLandName = (land) => {
  const fieldName = land?.field?.name || land?.field_name
  const landName = land?.name || ''

  return buildFieldLandDownloadSubject({fieldName, landName}) || landName
}

const getLandLabel = (land) => land?.name || getLandName(land)

const formatInputDate = (value) => {
  if (!value) {
    return '-'
  }

  const [year, month, day] = String(value).split('-')
  return `${day}/${month}/${year}`
}

const getContentKeyForImageType = (type) => `${type}_images`

const clampTechnicalReportProgress = (value) => Math.max(
  0,
  Math.min(100, Math.round(Number(value) || 0))
)

const createTechnicalReportProgressReporter = ({
  end = 100,
  onProgress,
  start = 0,
  total = 1,
}) => {
  if (typeof onProgress !== 'function') {
    return () => {}
  }

  const safeStart = clampTechnicalReportProgress(start)
  const safeEnd = clampTechnicalReportProgress(end)
  const safeTotal = Math.max(1, total)
  const progressSpan = Math.max(0, safeEnd - safeStart)
  let completed = 0

  onProgress(safeStart)

  return (steps = 1) => {
    completed = Math.min(safeTotal, completed + Math.max(1, steps))
    onProgress(clampTechnicalReportProgress(safeStart + (progressSpan * completed) / safeTotal))
  }
}

const countSelectedImageProgressSteps = ({
  content = {},
  imageSelections = {},
  imageTypes = IMAGE_TYPES,
  selectedLands = [],
}) => imageTypes.reduce((total, type) => {
    if (!content[getContentKeyForImageType(type)]) {
      return total
    }

    return total + selectedLands.reduce((landTotal, land) => {
      const landId = getLandId(land)
      return landTotal + (imageSelections?.[landId]?.[type] || []).length
    }, 0)
  }, 0)

const countTechnicalReportVisualProgressSteps = ({
  content = {},
  imageSelections = {},
  imageTypes = IMAGE_TYPES,
  selectedLands = [],
}) => {
  let total = Math.max(1, selectedLands.length)

  if (content.overview_map) {
    total += 1
  }

  total += countSelectedImageProgressSteps({content, imageSelections, imageTypes, selectedLands})

  if (content.altimetry) {
    total += selectedLands.length
  }

  if (content.soil_map) {
    total += selectedLands.length
  }

  if (content.historical_ambient) {
    total += selectedLands.length
  }

  if (content.image_ambient) {
    total += selectedLands.length
  }

  return Math.max(1, total)
}

const drawRoundedRect = (context, x, y, width, height, radius) => {
  const normalizedRadius = Math.min(radius, width / 2, height / 2)

  context.beginPath()
  context.moveTo(x + normalizedRadius, y)
  context.lineTo(x + width - normalizedRadius, y)
  context.quadraticCurveTo(x + width, y, x + width, y + normalizedRadius)
  context.lineTo(x + width, y + height - normalizedRadius)
  context.quadraticCurveTo(x + width, y + height, x + width - normalizedRadius, y + height)
  context.lineTo(x + normalizedRadius, y + height)
  context.quadraticCurveTo(x, y + height, x, y + height - normalizedRadius)
  context.lineTo(x, y + normalizedRadius)
  context.quadraticCurveTo(x, y, x + normalizedRadius, y)
  context.closePath()
}

const getAspectFitCanvasSize = ({
  heightRatio,
  maxHeight,
  maxWidth,
  minHeight = 220,
  minWidth = 220,
}) => {
  const safeHeightRatio =
    Number.isFinite(heightRatio) && heightRatio > 0 ? heightRatio : 1
  let width = maxWidth
  let height = Math.round(width * safeHeightRatio)

  if (height > maxHeight) {
    height = maxHeight
    width = Math.round(height / safeHeightRatio)
  }

  const minScale = Math.max(minWidth / Math.max(width, 1), minHeight / Math.max(height, 1), 1)
  width = Math.round(width * minScale)
  height = Math.round(height * minScale)

  const maxScale = Math.min(maxWidth / Math.max(width, 1), maxHeight / Math.max(height, 1), 1)
  width = Math.round(width * maxScale)
  height = Math.round(height * maxScale)

  return {
    height: Math.max(1, height),
    width: Math.max(1, width),
  }
}

const wrapText = (context, text, maxWidth) => {
  const words = String(text || '').split(/\s+/).filter(Boolean)
  const lines = []
  let current = ''

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word

    if (context.measureText(next).width <= maxWidth) {
      current = next
      return
    }

    if (current) {
      lines.push(current)
    }
    current = word
  })

  if (current) {
    lines.push(current)
  }

  return lines.length > 0 ? lines : ['']
}

const createDrawableImage = async (blob) => {
  if (typeof window.createImageBitmap === 'function') {
    return window.createImageBitmap(blob)
  }

  return new Promise((resolve, reject) => {
    const sourceUrl = URL.createObjectURL(blob)
    const image = new Image()
    const timeoutId = window.setTimeout(() => {
      URL.revokeObjectURL(sourceUrl)
      reject(new Error('technical-report-image-timeout'))
    }, 12000)

    image.onload = () => {
      window.clearTimeout(timeoutId)
      URL.revokeObjectURL(sourceUrl)
      resolve(image)
    }

    image.onerror = () => {
      window.clearTimeout(timeoutId)
      URL.revokeObjectURL(sourceUrl)
      reject(new Error('technical-report-image-load-failed'))
    }

    image.src = sourceUrl
  })
}

const disposeDrawableImage = (drawable) => {
  drawable?.close?.()
}

const createCanvasFromDrawable = async (blob) => {
  const drawable = await createDrawableImage(blob)
  const canvas = document.createElement('canvas')

  canvas.width = drawable.width
  canvas.height = drawable.height
  canvas.getContext('2d').drawImage(drawable, 0, 0, canvas.width, canvas.height)
  disposeDrawableImage(drawable)

  return canvas
}

const canvasToJpegDataUrl = (canvas, quality = 0.88) => {
  const ratio = Math.min(
    MAX_EXPORT_WIDTH / Math.max(canvas.width, 1),
    MAX_EXPORT_HEIGHT / Math.max(canvas.height, 1),
    1
  )
  const output = document.createElement('canvas')

  output.width = Math.max(1, Math.round(canvas.width * ratio))
  output.height = Math.max(1, Math.round(canvas.height * ratio))

  const context = output.getContext('2d')
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, output.width, output.height)
  context.drawImage(canvas, 0, 0, output.width, output.height)

  return {
    dataUrl: output.toDataURL('image/jpeg', quality),
    height: output.height,
    width: output.width,
  }
}

const normalizeImageLegend = (headers) => {
  if (!headers?.['x-index-range']) {
    return []
  }

  try {
    const intervals = JSON.parse(headers['x-index-range'])
    const totalHa = intervals.reduce((sum, interval) => sum + Number(interval.ha || 0), 0)

    return intervals
      .map((interval) => {
        const rawPercentage = Number(interval.percentage)
        const percentage = Number.isFinite(rawPercentage)
          ? rawPercentage > 1
            ? rawPercentage
            : rawPercentage * 100
          : totalHa > 0
            ? (Number(interval.ha || 0) * 100) / totalHa
            : 0

        return {
          cluster: interval.cluster,
          ha: Number(interval.ha || 0),
          percentage,
        }
      })
      .reverse()
  } catch (error) {
    return []
  }
}

const getAmbientColor = (key, numClasses) => {
  return {
    2: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(26,150,65)',
    },
    3: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(255,255,192)',
      pp: 'rgb(26,150,65)',
    },
    4: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(254,201,129)',
      pp: 'rgb(196,230,135)',
      ap: 'rgb(26,150,65)',
    },
    5: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(253,174,97)',
      pp: 'rgb(255,255,192)',
      ap: 'rgb(166,217,106)',
      map: 'rgb(26,150,65)',
    },
    6: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(246,144,83)',
      pp: 'rgb(255,223,154)',
      ap: 'rgb(220,240,158)',
      map: 'rgb(138,204,98)',
      five: 'rgb(26,150,65)',
    },
    7: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(241,124,74)',
      pp: 'rgb(254,201,129)',
      ap: 'rgb(255,255,192)',
      map: 'rgb(196,230,135)',
      five: 'rgb(119,195,92)',
      six: 'rgb(26,150,65)',
    },
  }[parseInt(numClasses, 10)]?.[key] || '#9ca3af'
}

const getAmbientValidClasses = (numClasses) => {
  return {
    2: ['mbp', 'bp'],
    3: ['mbp', 'bp', 'pp'],
    4: ['mbp', 'bp', 'pp', 'ap'],
    5: ['mbp', 'bp', 'pp', 'ap', 'map'],
    6: ['mbp', 'bp', 'pp', 'ap', 'map', 'five'],
    7: ['mbp', 'bp', 'pp', 'ap', 'map', 'five', 'six'],
  }[numClasses] || []
}

const getAmbientLegendRows = (ambient) => {
  if (!ambient?.geojson?.features?.length) {
    return []
  }

  const base = {
    mbp: {classIndex: 0, ha: 0, sort: 6},
    bp: {classIndex: 1, ha: 0, sort: 5},
    pp: {classIndex: 2, ha: 0, sort: 4},
    ap: {classIndex: 3, ha: 0, sort: 3},
    map: {classIndex: 4, ha: 0, sort: 2},
    five: {classIndex: 5, ha: 0, sort: 1},
    six: {classIndex: 6, ha: 0, sort: 0},
  }
  let total = 0

  ambient.geojson.features.forEach((feature) => {
    const currentHa = Number(feature.properties?.sup_ha || 0)
    total += currentHa

    switch (feature.properties?.cls) {
      case 0:
        base.mbp.ha += currentHa
        break
      case 1:
        base.bp.ha += currentHa
        break
      case 2:
        base.pp.ha += currentHa
        break
      case 3:
        base.ap.ha += currentHa
        break
      case 4:
        base.map.ha += currentHa
        break
      case 5:
        base.five.ha += currentHa
        break
      case 6:
        base.six.ha += currentHa
        break
      default:
        break
    }
  })

  const numClasses = getAmbientClassCount(ambient)

  return Object.entries(base)
    .map(([key, value]) => ({
      ...value,
      color: getAmbientColor(key, numClasses),
      key,
      classNumber: getAmbientDisplayClassNumber(value.classIndex, numClasses),
      percentage: total > 0 ? (value.ha * 100) / total : 0,
      total,
    }))
    .sort((left, right) => left.sort - right.sort)
    .filter((entry) => getAmbientValidClasses(numClasses).includes(entry.key))
}

const collectCoordinatePairs = (value, output = []) => {
  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Number.isFinite(Number(value.lng ?? value.lon ?? value.longitude)) &&
    Number.isFinite(Number(value.lat ?? value.latitude))
  ) {
    output.push([
      Number(value.lng ?? value.lon ?? value.longitude),
      Number(value.lat ?? value.latitude),
    ])
    return output
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const nestedGeometry =
      value.coordinates ??
      value.geometry ??
      value.geojson ??
      value.polygon ??
      value.polygons ??
      value.features

    if (nestedGeometry) {
      return collectCoordinatePairs(nestedGeometry, output)
    }

    return output
  }

  if (!Array.isArray(value)) {
    return output
  }

  if (
    value.length >= 2 &&
    Number.isFinite(Number(value[0])) &&
    Number.isFinite(Number(value[1]))
  ) {
    output.push([Number(value[0]), Number(value[1])])
    return output
  }

  value.forEach((item) => collectCoordinatePairs(item, output))
  return output
}

const isCoordinatePair = (value) =>
  Array.isArray(value) &&
  value.length >= 2 &&
  Number.isFinite(Number(value[0])) &&
  Number.isFinite(Number(value[1]))

const isLinearRingCoordinates = (value) =>
  Array.isArray(value) &&
  value.length >= 3 &&
  value.every(isCoordinatePair)

const isPolygonCoordinates = (value) =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(isLinearRingCoordinates)

const isMultiPolygonCoordinates = (value) =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(isPolygonCoordinates)

const hasGeometryShape = (value) =>
  value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  (value.type || value.geometry || value.coordinates || value.features)

const mergePolygonGeometries = (geometries) => {
  const coordinates = geometries.flatMap((geometry) => {
    if (geometry.type === 'Polygon') {
      return [geometry.coordinates]
    }

    if (geometry.type === 'MultiPolygon') {
      return geometry.coordinates
    }

    return []
  })

  if (coordinates.length < 1) {
    return null
  }

  if (coordinates.length === 1) {
    return {
      coordinates: coordinates[0],
      type: 'Polygon',
    }
  }

  return {
    coordinates,
    type: 'MultiPolygon',
  }
}

const geometryHasCoordinatePairs = (geometry) =>
  collectCoordinatePairs(geometry?.coordinates).length >= 3

const stripOuterParentheses = (value) => {
  let output = String(value || '').trim()

  while (output.startsWith('(') && output.endsWith(')')) {
    let depth = 0
    let wrapsWholeValue = true

    for (let index = 0; index < output.length; index += 1) {
      const char = output[index]

      if (char === '(') {
        depth += 1
      } else if (char === ')') {
        depth -= 1
      }

      if (depth === 0 && index < output.length - 1) {
        wrapsWholeValue = false
        break
      }
    }

    if (!wrapsWholeValue) {
      break
    }

    output = output.slice(1, -1).trim()
  }

  return output
}

const splitWktGroups = (value) => {
  const groups = []
  let depth = 0
  let start = null

  String(value || '').split('').forEach((char, index) => {
    if (char === '(') {
      if (depth === 0) {
        start = index + 1
      }

      depth += 1
      return
    }

    if (char === ')') {
      depth -= 1

      if (depth === 0 && start !== null) {
        groups.push(value.slice(start, index))
        start = null
      }
    }
  })

  return groups
}

const parseWktRing = (value) => {
  const coordinates = String(value || '')
    .split(',')
    .map((pair) => {
      const [lng, lat] = pair.trim().split(/\s+/).map(Number)

      return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null
    })
    .filter(Boolean)

  return coordinates.length >= 3 ? coordinates : null
}

const parseWktPolygonBody = (value) => {
  const groups = splitWktGroups(value)
  const ringSources = groups.length > 0 ? groups : [stripOuterParentheses(value)]

  return ringSources.map(parseWktRing).filter(Boolean)
}

const parseWktGeometry = (value) => {
  const normalized = String(value || '').replace(/^SRID=\d+;/i, '').trim()
  const upper = normalized.toUpperCase()

  if (upper.startsWith('POLYGON')) {
    const body = normalized.slice(normalized.indexOf('('))
    const coordinates = parseWktPolygonBody(body)

    return coordinates.length > 0 ? {coordinates, type: 'Polygon'} : null
  }

  if (upper.startsWith('MULTIPOLYGON')) {
    const body = stripOuterParentheses(normalized.slice(normalized.indexOf('(')))
    const coordinates = splitWktGroups(body)
      .map(parseWktPolygonBody)
      .filter((polygon) => polygon.length > 0)

    return coordinates.length > 0 ? {coordinates, type: 'MultiPolygon'} : null
  }

  return null
}

const parseGeometryValue = (value) => {
  if (typeof value !== 'string') {
    return value
  }

  const trimmedValue = value.trim()
  const withoutSrid = trimmedValue.replace(/^SRID=\d+;/i, '').trim()

  if (!withoutSrid || !/^[{[]/.test(withoutSrid)) {
    return parseWktGeometry(withoutSrid) || value
  }

  try {
    return JSON.parse(withoutSrid)
  } catch (error) {
    return value
  }
}

const normalizeGeometry = (value) => {
  if (!value) {
    return null
  }

  const parsedValue = parseGeometryValue(value)

  if (parsedValue !== value) {
    return normalizeGeometry(parsedValue)
  }

  if (value.type === 'Feature') {
    return findFirstGeometry([
      value.geometry,
      value.properties?.geometry,
      value.properties?.geojson,
      value.properties?.polygon,
      value.properties?.polygons,
      value.properties?.geom,
      value.properties?.the_geom,
      value.properties?.shape,
      value.properties?.boundary,
      value.properties?.perimeter,
      value.properties?.area,
    ])
  }

  if (value.type === 'FeatureCollection') {
    const polygons = (value.features || [])
      .map((feature) => normalizeGeometry(feature))
      .filter(Boolean)

    return mergePolygonGeometries(polygons)
  }

  if (value.type === 'Polygon' || value.type === 'MultiPolygon') {
    const parsedCoordinates = parseGeometryValue(value.coordinates)
    const geometry = {
      ...value,
      coordinates: parsedCoordinates === value.coordinates ? value.coordinates : parsedCoordinates,
    }

    return geometryHasCoordinatePairs(geometry) ? geometry : null
  }

  if (Array.isArray(value)) {
    if (value.some(hasGeometryShape)) {
      return mergePolygonGeometries(value.map((item) => normalizeGeometry(item)).filter(Boolean))
    }

    if (isLinearRingCoordinates(value)) {
      return {
        coordinates: [value],
        type: 'Polygon',
      }
    }

    if (isPolygonCoordinates(value)) {
      return {
        coordinates: value,
        type: 'Polygon',
      }
    }

    if (isMultiPolygonCoordinates(value)) {
      return {
        coordinates: value,
        type: 'MultiPolygon',
      }
    }

    if (collectCoordinatePairs(value).length >= 3) {
      return {
        coordinates: [collectCoordinatePairs(value)],
        type: 'Polygon',
      }
    }
  }

  if (typeof value === 'object') {
    return findFirstGeometry([
      value.polygons,
      value.polygon,
      value.geometry,
      value.geojson,
      value.feature,
      value.features,
      value.coordinates,
      value.geom,
      value.the_geom,
      value.shape,
      value.boundary,
      value.perimeter,
      value.area,
      value.attributes?.geometry,
      value.attributes?.geojson,
      value.attributes?.polygon,
      value.attributes?.polygons,
      value.properties?.geometry,
      value.properties?.geojson,
      value.properties?.polygon,
      value.properties?.polygons,
      value.properties?.geom,
      value.properties?.the_geom,
      value.properties?.shape,
      value.properties?.boundary,
      value.properties?.perimeter,
      value.properties?.area,
    ])
  }

  return null
}

const findFirstGeometry = (values = []) => {
  for (const value of values) {
    const geometry = normalizeGeometry(value)

    if (geometry) {
      return geometry
    }
  }

  return null
}

const getLandGeometry = (land) =>
  findFirstGeometry([
    land?.polygons,
    land?.polygon,
    land?.geometry,
    land?.geometry_json,
    land?.geometry_geojson,
    land?.geojson,
    land?.feature,
    land?.features,
    land?.coordinates,
    land?.geom,
    land?.the_geom,
    land?.shape,
    land?.boundary,
    land?.perimeter,
    land?.area,
    land?.data?.polygons,
    land?.data?.polygon,
    land?.data?.geometry,
    land?.data?.geometry_json,
    land?.data?.geometry_geojson,
    land?.data?.geojson,
    land?.data?.feature,
    land?.data?.features,
    land?.data?.coordinates,
    land?.data?.geom,
    land?.data?.the_geom,
    land?.data?.shape,
    land?.data?.boundary,
    land?.data?.perimeter,
    land?.data?.area,
    land?.land?.polygons,
    land?.land?.polygon,
    land?.land?.geometry,
    land?.land?.geojson,
    land?.gis?.polygons,
    land?.gis?.polygon,
    land?.gis?.geometry,
    land?.gis?.geojson,
    land?.attributes?.geometry,
    land?.attributes?.geojson,
    land?.properties?.geometry,
    land?.properties?.geometry_json,
    land?.properties?.geometry_geojson,
    land?.properties?.geojson,
  ])

const getFeatureGeometry = (feature) =>
  findFirstGeometry([
    feature?.geometry,
    feature?.properties?.geometry,
    feature?.properties?.geometry_json,
    feature?.properties?.geometry_geojson,
    feature?.properties?.geojson,
    feature?.properties?.coordinates,
    feature?.properties?.polygon,
    feature?.properties?.polygons,
    feature?.properties?.geom,
    feature?.properties?.the_geom,
    feature?.properties?.shape,
    feature?.properties?.boundary,
    feature?.properties?.perimeter,
    feature?.properties?.area,
  ])

const setLandGeometry = (land, geometry) => ({
  ...land,
  polygons: geometry || getLandGeometry(land),
})

const sameLandFeature = (feature, land) => {
  const resourceType = getFeatureResourceType(feature)
  const isLandFeature = !resourceType || resourceType === 'land'
  const fieldId = getFieldId(land)

  if (isLandFeature && valuesIntersect(getFeatureCandidateIds(feature), getLandCandidateIds(land))) {
    return true
  }

  if (!land) {
    return false
  }

  const landNames = getLandCandidateNames(land)
  const featureNames = getFeatureCandidateNames(feature)
  const landFieldIds = getFieldCandidateIds(land)
  const featureFieldIds = getFeatureFieldCandidateIds(feature)
  const landFieldNames = getFieldCandidateNames(land)
  const featureFieldNames = getFeatureFieldCandidateNames(feature)
  const fieldIdMatches =
    !fieldId ||
    featureFieldIds.length === 0 ||
    valuesIntersect(featureFieldIds, landFieldIds)
  const fieldNameMatches =
    featureFieldNames.length === 0 ||
    landFieldNames.length === 0 ||
    featureFieldNames.some((fieldName) => landFieldNames.includes(fieldName))

  return Boolean(
    isLandFeature &&
    landNames.length > 0 &&
    featureNames.some((featureName) => landNames.includes(featureName)) &&
    fieldIdMatches &&
    fieldNameMatches
  )
}

const getMapStateLandGeometry = (land) => {
  try {
    const landIds = getLandCandidateIds(land)

    if (landIds.length === 0 || !mapState?.isInitialized?.()) {
      return null
    }

    const source = mapState.source()
    const directFeature = landIds.map((landId) => source.getFeature(landId)).find(Boolean)

    const directGeometry = getFeatureGeometry(directFeature)

    if (directGeometry) {
      return directGeometry
    }

    const fallbackFeature = (source.get()?.features || []).find((feature) => sameLandFeature(feature, land))

    return getFeatureGeometry(fallbackFeature)
  } catch (error) {
    return null
  }
}

const getPolygonImageBbox = (polygons) => {
  const coordinates = collectCoordinatePairs(polygons)

  if (coordinates.length < 3) {
    return null
  }

  const lngs = coordinates.map(([lng]) => lng)
  const lats = coordinates.map(([, lat]) => lat)
  const minLng = Math.min(...lngs)
  const maxLng = Math.max(...lngs)
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)

  if (minLng === maxLng || minLat === maxLat) {
    return null
  }

  return [
    [minLng, maxLat],
    [maxLng, maxLat],
    [maxLng, minLat],
    [minLng, minLat],
  ]
}

const getCoordinateBboxCenter = (coordinates) => {
  if (!coordinates.length) {
    return null
  }

  const lngs = coordinates.map(([lng]) => lng)
  const lats = coordinates.map(([, lat]) => lat)

  return [
    (Math.min(...lngs) + Math.max(...lngs)) / 2,
    (Math.min(...lats) + Math.max(...lats)) / 2,
  ]
}

const expandBbox = (bbox, ratio = 0.55) => {
  const minLng = bbox[0][0]
  const maxLng = bbox[1][0]
  const maxLat = bbox[0][1]
  const minLat = bbox[2][1]
  const lngSpan = Math.max(maxLng - minLng, 0.0008)
  const latSpan = Math.max(maxLat - minLat, 0.0008)
  const lngPadding = lngSpan * ratio
  const latPadding = latSpan * ratio

  return [
    [minLng - lngPadding, maxLat + latPadding],
    [maxLng + lngPadding, maxLat + latPadding],
    [maxLng + lngPadding, minLat - latPadding],
    [minLng - lngPadding, minLat - latPadding],
  ]
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const getMercatorPixel = ([lng, lat], zoom) => {
  const scale = TILE_SIZE * (2 ** zoom)
  const normalizedLat = clamp(lat, -85.05112878, 85.05112878)
  const radians = (normalizedLat * Math.PI) / 180

  return {
    x: ((lng + 180) / 360) * scale,
    y: (
      1 -
      Math.log(Math.tan(radians) + (1 / Math.cos(radians))) / Math.PI
    ) / 2 * scale,
  }
}

const getOverviewZoom = (bbox, width, height) => {
  for (let zoom = 19; zoom >= 2; zoom -= 1) {
    const northWest = getMercatorPixel(bbox[0], zoom)
    const southEast = getMercatorPixel(bbox[2], zoom)

    if (
      Math.abs(southEast.x - northWest.x) <= width * OVERVIEW_CANVAS_FILL_RATIO &&
      Math.abs(southEast.y - northWest.y) <= height * OVERVIEW_CANVAS_FILL_RATIO
    ) {
      return zoom
    }
  }

  return 2
}

const createMercatorProjector = (bbox, width, height, zoom) => {
  const centerLng = (bbox[0][0] + bbox[1][0]) / 2
  const centerLat = (bbox[0][1] + bbox[2][1]) / 2
  const center = getMercatorPixel([centerLng, centerLat], zoom)
  const topLeft = {
    x: center.x - width / 2,
    y: center.y - height / 2,
  }

  return {
    project: (coordinate) => {
      const point = getMercatorPixel(coordinate, zoom)

      return {
        x: point.x - topLeft.x,
        y: point.y - topLeft.y,
      }
    },
    topLeft,
    zoom,
  }
}

const getOverviewLandLabelMetrics = (context, label) => {
  const text = String(label || '').trim()

  if (!text) {
    return null
  }

  const paddingX = 12
  const height = 30
  let fontSize = 15

  context.font = `700 ${fontSize}px Poppins, Arial, sans-serif`

  while (
    fontSize > 10 &&
    context.measureText(text).width + paddingX * 2 > OVERVIEW_LABEL_MAX_WIDTH
  ) {
    fontSize -= 1
    context.font = `700 ${fontSize}px Poppins, Arial, sans-serif`
  }

  const measuredWidth = context.measureText(text).width
  const width = Math.min(measuredWidth + paddingX * 2, OVERVIEW_LABEL_MAX_WIDTH)

  return {
    font: `700 ${fontSize}px Poppins, Arial, sans-serif`,
    height,
    text,
    width,
  }
}

const labelsOverlap = (left, right, padding = 8) =>
  Math.abs(left.point.y - right.point.y) < (left.metrics.height + right.metrics.height) / 2 + padding &&
  Math.abs(left.point.x - right.point.x) < (left.metrics.width + right.metrics.width) / 2 + padding

const resolveOverviewLabelPositions = (labelItems, width, height) => {
  const positionedLabels = labelItems.map((item) => ({
    ...item,
    point: {
      x: clamp(item.point.x, item.metrics.width / 2 + 4, width - item.metrics.width / 2 - 4),
      y: clamp(item.point.y, item.metrics.height / 2 + 4, height - item.metrics.height / 2 - 4),
    },
  }))
  const sortedLabels = [...positionedLabels].sort((left, right) => left.point.x - right.point.x)

  sortedLabels.forEach((item, index) => {
    for (let previousIndex = 0; previousIndex < index; previousIndex += 1) {
      const previous = sortedLabels[previousIndex]

      if (!labelsOverlap(previous, item)) {
        continue
      }

      const minX =
        previous.point.x +
        previous.metrics.width / 2 +
        item.metrics.width / 2 +
        8

      item.point.x = Math.max(item.point.x, minX)
    }
  })

  const overflowRight = Math.max(
    0,
    ...sortedLabels.map((item) => item.point.x + item.metrics.width / 2 + 4 - width)
  )

  if (overflowRight > 0) {
    sortedLabels.forEach((item) => {
      item.point.x -= overflowRight
    })
  }

  const overflowLeft = Math.max(
    0,
    ...sortedLabels.map((item) => item.metrics.width / 2 + 4 - item.point.x)
  )

  if (overflowLeft > 0) {
    sortedLabels.forEach((item) => {
      item.point.x += overflowLeft
    })
  }

  return positionedLabels
}

const drawOverviewLandLabel = (context, label, point, metrics) => {
  const labelMetrics = metrics || getOverviewLandLabelMetrics(context, label)

  if (!labelMetrics || !point) {
    return
  }

  const x = point.x - labelMetrics.width / 2
  const y = point.y - labelMetrics.height / 2

  drawRoundedRect(context, x, y, labelMetrics.width, labelMetrics.height, labelMetrics.height / 2)
  context.fillStyle = 'rgba(238, 243, 248, 0.96)'
  context.fill()
  context.strokeStyle = 'rgba(30, 41, 59, 0.16)'
  context.lineWidth = 1.5
  context.stroke()

  context.font = labelMetrics.font
  context.textAlign = 'center'
  context.textBaseline = 'middle'
  context.fillStyle = '#2f3b52'
  context.fillText(labelMetrics.text, point.x, point.y + 0.5)
  context.textAlign = 'left'
  context.textBaseline = 'alphabetic'
}

const getGoogleTileUrl = (x, y, zoom) => {
  const server = GOOGLE_TILE_SERVERS[Math.abs(x + y + zoom) % GOOGLE_TILE_SERVERS.length]

  return `https://${server}.google.com/vt/lyrs=s,h&x=${x}&y=${y}&z=${zoom}`
}

const loadImageDrawable = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    const timeoutId = window.setTimeout(() => {
      reject(new Error('technical-report-google-tile-timeout'))
    }, 12000)

    image.crossOrigin = 'anonymous'
    image.onload = () => {
      window.clearTimeout(timeoutId)
      resolve(image)
    }
    image.onerror = () => {
      window.clearTimeout(timeoutId)
      reject(new Error('technical-report-google-tile-image-failed'))
    }
    image.src = url
  })

const loadTileDrawable = async (url) => {
  try {
    const response = await fetch(url, {mode: 'cors'})

    if (!response.ok) {
      throw new Error('technical-report-google-tile-failed')
    }

    return createDrawableImage(await response.blob())
  } catch (error) {
    return loadImageDrawable(url)
  }
}

const drawGoogleOverviewTiles = async (context, bbox, width, height) => {
  const zoom = getOverviewZoom(bbox, width, height)
  const {project, topLeft} = createMercatorProjector(bbox, width, height, zoom)
  const worldTiles = 2 ** zoom
  const startX = Math.floor(topLeft.x / TILE_SIZE)
  const endX = Math.floor((topLeft.x + width) / TILE_SIZE)
  const startY = clamp(Math.floor(topLeft.y / TILE_SIZE), 0, worldTiles - 1)
  const endY = clamp(Math.floor((topLeft.y + height) / TILE_SIZE), 0, worldTiles - 1)
  let drewTile = false

  for (let tileX = startX; tileX <= endX; tileX += 1) {
    for (let tileY = startY; tileY <= endY; tileY += 1) {
      const wrappedX = ((tileX % worldTiles) + worldTiles) % worldTiles

      try {
        const drawable = await loadTileDrawable(getGoogleTileUrl(wrappedX, tileY, zoom))
        context.drawImage(
          drawable,
          Math.round(tileX * TILE_SIZE - topLeft.x),
          Math.round(tileY * TILE_SIZE - topLeft.y),
          TILE_SIZE,
          TILE_SIZE
        )
        disposeDrawableImage(drawable)
        drewTile = true
      } catch (error) {
        if (!drewTile) {
          return null
        }
      }
    }
  }

  return drewTile ? project : null
}

const getCanvasPointFromCoordinate = ([lng, lat], bbox, width, height) => {
  const minLng = bbox[0][0]
  const maxLng = bbox[1][0]
  const maxLat = bbox[0][1]
  const minLat = bbox[2][1]
  const lngSpan = maxLng - minLng
  const latSpan = maxLat - minLat

  if (!lngSpan || !latSpan) {
    return null
  }

  return {
    x: ((lng - minLng) / lngSpan) * width,
    y: ((maxLat - lat) / latSpan) * height,
  }
}

const getAmbientFeaturePolygons = (geometry) => {
  if (geometry?.type === 'Polygon') {
    return [geometry.coordinates]
  }

  if (geometry?.type === 'MultiPolygon') {
    return geometry.coordinates
  }

  return []
}

const getAmbientCanvasSize = (bbox) => {
  const lngSpan = Math.abs(bbox[1][0] - bbox[0][0]) || 1
  const latSpan = Math.abs(bbox[0][1] - bbox[2][1]) || 1

  return getAspectFitCanvasSize({
    heightRatio: latSpan / lngSpan,
    maxHeight: 980,
    maxWidth: 760,
  })
}

const createCanvasFromAmbientGeojson = (geojson, bbox) => {
  if (!geojson?.features?.length || !bbox) {
    return null
  }

  const {height, width} = getAmbientCanvasSize(bbox)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')
  context.clearRect(0, 0, width, height)

  geojson.features.forEach((feature) => {
    const color = feature.properties?.color
    const polygons = getAmbientFeaturePolygons(feature.geometry)

    if (!color || !polygons.length) {
      return
    }

    context.beginPath()
    polygons.forEach((polygon) => {
      polygon.forEach((ring) => {
        ring.forEach((coordinate, index) => {
          const point = getCanvasPointFromCoordinate(coordinate, bbox, width, height)

          if (!point) {
            return
          }

          if (index === 0) {
            context.moveTo(point.x, point.y)
            return
          }

          context.lineTo(point.x, point.y)
        })
        context.closePath()
      })
    })
    context.fillStyle = color
    context.fill('evenodd')
  })

  return canvas
}

const getLegendTotalHa = (rows) => rows.reduce((sum, row) => sum + Number(row.ha || 0), 0)

const getLegendHeight = (rows, legendType = 'image') => {
  if (!rows.length) {
    return 0
  }

  const rowHeight = 36
  const top = legendType === 'ambient' ? 120 : 92
  const footer = legendType === 'ambient' ? 78 : 56

  return top + rows.length * rowHeight + footer
}

const getAltimetryLegendHeight = (width) =>
  Math.round(ALTIMETRY_LEGEND_HEIGHT * (width / ALTIMETRY_LEGEND_WIDTH))

const drawScaledAltimetryLegend = (context, legend, x, y, width) => {
  const scale = width / ALTIMETRY_LEGEND_WIDTH

  context.save()
  context.translate(x, y)
  context.scale(scale, scale)
  drawAltimetryLegend(context, legend, 0, 0, ALTIMETRY_LEGEND_WIDTH)
  context.restore()
}

const drawLegendRows = (
  context,
  rows,
  x,
  y,
  width,
  labels = {},
  legendType = 'image',
  legendEnhanced = false
) => {
  const totalHeight = getLegendHeight(rows, legendType)

  if (!totalHeight) {
    return 0
  }

  const isAmbientLegend = legendType === 'ambient'
  const rowHeight = 36
  const tableTop = y + (isAmbientLegend ? 124 : 92)
  const haX = x + width - 104
  const percentX = x + width - 24

  context.save()
  context.shadowColor = 'rgba(15, 23, 42, 0.10)'
  context.shadowBlur = 18
  context.shadowOffsetY = 8
  drawRoundedRect(context, x, y, width, totalHeight, 18)
  context.fillStyle = '#ffffff'
  context.fill()
  context.restore()

  drawRoundedRect(context, x, y, width, totalHeight, 18)
  context.strokeStyle = 'rgba(15, 23, 42, 0.08)'
  context.lineWidth = 1
  context.stroke()

  context.fillStyle = '#101426'
  context.font = '700 26px Poppins, Arial, sans-serif'
  context.textBaseline = 'top'
  context.fillText(labels.legend || 'Leyenda', x + 22, y + 20)

  if (legendType === 'image' && legendEnhanced) {
    context.fillStyle = '#8a91ab'
    context.font = '600 18px Poppins, Arial, sans-serif'
    context.textAlign = 'right'
    context.fillText(labels.enhanced || 'Realzada', x + width - 22, y + 24)
    context.textAlign = 'left'
  }

  context.fillStyle = '#101426'
  context.font = '700 17px Poppins, Arial, sans-serif'
  context.fillText(isAmbientLegend ? 'CLASE' : 'ÍNDICE', x + 58, y + 60)
  context.textAlign = 'right'
  context.fillText('HA', haX, y + 60)
  context.fillText('%', percentX, y + 60)
  context.textAlign = 'left'

  if (isAmbientLegend) {
    context.fillStyle = '#8a91ab'
    context.font = '800 14px Poppins, Arial, sans-serif'
    context.fillText('MAYOR PRODUCTIVIDAD', x + 22, y + 88)
  }

  rows.forEach((row, index) => {
    const rowY = tableTop + index * rowHeight

    context.fillStyle = row.color || IMAGE_LEGEND_COLORS[index] || '#9ca3af'
    drawRoundedRect(context, x + 22, rowY, 21, 21, 5)
    context.fill()

    context.fillStyle = '#172033'
    context.font = '700 18px Poppins, Arial, sans-serif'
    context.fillText(row.label || row.cluster || `${labels.class || 'Clase'} ${index + 1}`, x + 62, rowY)

    context.textAlign = 'right'
    context.font = '500 18px Poppins, Arial, sans-serif'
    context.fillText(formatFixedNumber(row.ha), haX, rowY)
    context.fillText(formatPercent(row.percentage), percentX, rowY)
    context.textAlign = 'left'
  })

  const lowerLabelTop = tableTop + rows.length * rowHeight + 2
  const totalTop = lowerLabelTop + (isAmbientLegend ? 22 : 8)

  if (isAmbientLegend) {
    context.fillStyle = '#8a91ab'
    context.font = '800 14px Poppins, Arial, sans-serif'
    context.fillText('MENOR PRODUCTIVIDAD', x + 22, lowerLabelTop)
  }

  context.strokeStyle = '#eef1f6'
  context.lineWidth = 1
  context.beginPath()
  context.moveTo(x + 22, totalTop - 8)
  context.lineTo(x + width - 22, totalTop - 8)
  context.stroke()

  context.fillStyle = '#172033'
  context.font = '700 18px Poppins, Arial, sans-serif'
  context.fillText('Total', x + 62, totalTop)
  context.textAlign = 'right'
  context.fillText(formatFixedNumber(getLegendTotalHa(rows)), haX, totalTop)
  context.fillText(formatPercent(100), percentX, totalTop)
  context.textAlign = 'left'

  return totalHeight
}

const drawFigure = ({
  canvas,
  altimetryLegend = null,
  labels,
  legendEnhanced = false,
  legendRows = [],
  legendType = 'image',
  soilMapLegend = null,
  subtitle,
  title,
  variant = 'standard',
}) => {
  const hasAltimetryLegend = legendType === 'altimetry' && Boolean(altimetryLegend)
  const hasSoilMapLegend = legendType === 'soil_map' && Boolean(soilMapLegend)
  const hasLegend = legendRows.length > 0 || hasAltimetryLegend || hasSoilMapLegend
  const outputWidth = 1120
  const padding = 34
  const legendWidth = hasLegend
    ? (legendType === 'ambient' || hasAltimetryLegend || hasSoilMapLegend ? 390 : 380)
    : 0
  const gap = hasLegend ? 36 : 0
  const bodyMaxWidth = outputWidth - padding * 2
  const imageMaxWidth = bodyMaxWidth - legendWidth - gap
  const measure = document.createElement('canvas').getContext('2d')
  measure.font = '700 26px Poppins, Arial, sans-serif'
  const titleLines = wrapText(measure, title, outputWidth - padding * 2)
  measure.font = '700 22px Poppins, Arial, sans-serif'
  const subtitleLines = subtitle ? wrapText(measure, subtitle, outputWidth - padding * 2) : []
  const headerHeight = 26 + titleLines.length * 32 + (subtitleLines.length ? subtitleLines.length * 28 + 12 : 14)
  const bodyMaxHeight = variant === 'overview'
    ? OVERVIEW_FIGURE_MAX_BODY_HEIGHT
    : STANDARD_FIGURE_MAX_BODY_HEIGHT
  const imageRatio = Math.min(
    imageMaxWidth / Math.max(canvas.width, 1),
    bodyMaxHeight / Math.max(canvas.height, 1),
    hasLegend ? 3.8 : 5.2
  )
  const imageWidth = Math.round(canvas.width * imageRatio)
  const imageHeight = Math.round(canvas.height * imageRatio)
  let legendHeight = getLegendHeight(legendRows, legendType)

  if (hasAltimetryLegend) {
    legendHeight = getAltimetryLegendHeight(legendWidth)
  } else if (hasSoilMapLegend) {
    legendHeight = getSoilMapLegendHeight(soilMapLegend, labels.soilMap, legendWidth)
  }
  const bodyHeight = Math.max(imageHeight, legendHeight)
  const outputHeight = headerHeight + bodyHeight + padding
  const output = document.createElement('canvas')

  output.width = outputWidth
  output.height = outputHeight

  const context = output.getContext('2d')
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, outputWidth, outputHeight)

  context.fillStyle = '#101426'
  context.font = '700 26px Poppins, Arial, sans-serif'
  context.textBaseline = 'top'
  titleLines.forEach((line, index) => {
    context.fillText(line, padding, 26 + index * 32)
  })

  if (subtitleLines.length) {
    context.fillStyle = '#7f87a3'
    context.font = '700 22px Poppins, Arial, sans-serif'
    subtitleLines.forEach((line, index) => {
      context.fillText(line, padding, 26 + titleLines.length * 32 + 6 + index * 28)
    })
  }

  const bodyWidth = imageWidth + (hasLegend ? gap + legendWidth : 0)
  const imageX = padding + Math.max(0, Math.round((bodyMaxWidth - bodyWidth) / 2))
  const imageY = headerHeight

  context.save()
  context.fillStyle = '#f4f7fb'
  context.fillRect(imageX - 1, imageY - 1, imageWidth + 2, imageHeight + 2)
  context.drawImage(canvas, imageX, imageY, imageWidth, imageHeight)
  context.restore()

  if (hasAltimetryLegend) {
    drawScaledAltimetryLegend(
      context,
      altimetryLegend,
      imageX + imageWidth + gap,
      imageY + Math.max(0, Math.round((bodyHeight - legendHeight) / 2)),
      legendWidth
    )
  } else if (hasSoilMapLegend) {
    drawSoilMapLegend(
      context,
      soilMapLegend,
      labels.soilMap,
      imageX + imageWidth + gap,
      imageY + Math.max(0, Math.round((bodyHeight - legendHeight) / 2)),
      legendWidth
    )
  } else if (hasLegend) {
    drawLegendRows(
      context,
      legendRows,
      imageX + imageWidth + gap,
      imageY + Math.max(0, Math.round((bodyHeight - legendHeight) / 2)),
      legendWidth,
      labels,
      legendType,
      legendEnhanced
    )
  }

  return canvasToJpegDataUrl(output, 0.94)
}

const enrichLand = async (land) => {
  if (getLandGeometry(land)) {
    return setLandGeometry(land)
  }

  const landId = getLandId(land)

  if (!landId) {
    return land
  }

  try {
    const res = await getLand(landId)

    if (!res?.ok || !res.data) {
      return land
    }

    const enrichedLand = setLandGeometry({
      ...land,
      ...res.data,
      field: land.field || res.data.field,
      id: land.id || res.data.id || res.data.land_id,
      land_id: land.land_id || res.data.land_id || res.data.id,
      name: land.name || res.data.name,
    })

    if (getLandGeometry(enrichedLand)) {
      return enrichedLand
    }
  } catch (error) {
    // Try the field bundle below before giving up.
  }

  const fieldId = getFieldId(land)

  if (!fieldId) {
    return land
  }

  try {
    const res = await listFieldLands(fieldId)
    const detailedLand = (res?.ok ? res.data || [] : []).find((item) => {
      return (
        valuesIntersect(getLandCandidateIds(item), getLandCandidateIds(land)) ||
        sameLandFeature({properties: item}, land)
      )
    })

    if (!detailedLand) {
      return land
    }

    return setLandGeometry({
      ...land,
      ...detailedLand,
      field: land.field || detailedLand.field,
      id: land.id || detailedLand.id || detailedLand.land_id,
      land_id: land.land_id || detailedLand.land_id || detailedLand.id,
      name: land.name || detailedLand.name,
    })
  } catch (error) {
    return land
  }
}

const enrichLandsWithMapGeojson = async (lands) => {
  const enrichedLands = lands.map((land) => {
    const indexedLand = setLandGeometry(land, getMapStateLandGeometry(land))

    return indexedLand
  })
  const byLandId = enrichedLands.reduce((accumulator, land) => {
    indexLandByCandidateIds(accumulator, land)

    return accumulator
  }, {})
  const replaceEnrichedLand = (currentLand, nextLand) => {
    const index = enrichedLands.findIndex((land) => (
      land === currentLand ||
      valuesIntersect(getLandCandidateIds(land), getLandCandidateIds(currentLand)) ||
      sameLandFeature({properties: land}, currentLand)
    ))

    if (index >= 0) {
      enrichedLands[index] = nextLand
    }
  }

  try {
    const res = await GeoJsonService.fetch()
    const features = res?.ok ? getGeoJsonFeatures(res.data) : []

    features.forEach((feature) => {
      const matchingLand =
        findIndexedLandByCandidateIds(byLandId, getFeatureCandidateIds(feature)) ||
        enrichedLands.find((land) => sameLandFeature(feature, land))
      const currentLand =
        (matchingLand && findIndexedLandByCandidateIds(byLandId, getLandCandidateIds(matchingLand))) ||
        matchingLand
      const geometry = getFeatureGeometry(feature)

      if (!matchingLand || !currentLand || getLandGeometry(currentLand) || !geometry) {
        return
      }

      const nextLand = setLandGeometry(currentLand, geometry)

      indexLandByCandidateIds(byLandId, nextLand)
      replaceEnrichedLand(currentLand, nextLand)
    })
  } catch (error) {
    return lands
  }

  const outputLands = enrichedLands.map((land) => findIndexedLandByCandidateIds(byLandId, getLandCandidateIds(land)) || land)

  return outputLands
}

const createOverviewCanvas = async (lands) => {
  const landsWithGeometry = lands
    .map((land) => setLandGeometry(land))
    .filter((land) => getLandGeometry(land))
  const coordinates = landsWithGeometry.flatMap((land) => collectCoordinatePairs(getLandGeometry(land)))

  if (coordinates.length < 3) {
    return null
  }

  const polygonBbox = getPolygonImageBbox(coordinates)

  if (!polygonBbox) {
    return null
  }

  const bbox = expandBbox(polygonBbox, OVERVIEW_BBOX_PADDING_RATIO)
  const width = 920
  const height = 560
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')
  context.fillStyle = '#dce7ef'
  context.fillRect(0, 0, width, height)

  const googleProject = await drawGoogleOverviewTiles(context, bbox, width, height)
  const project = googleProject || ((coordinate) => getCanvasPointFromCoordinate(coordinate, bbox, width, height))

  if (!googleProject) {
    const gradient = context.createLinearGradient(0, 0, width, height)
    gradient.addColorStop(0, '#d6e1d3')
    gradient.addColorStop(0.45, '#c9d2b1')
    gradient.addColorStop(1, '#aabf98')
    context.fillStyle = gradient
    context.fillRect(0, 0, width, height)
    context.fillStyle = 'rgba(255, 255, 255, 0.18)'
    for (let x = -80; x < width + 80; x += 92) {
      context.fillRect(x, 0, 32, height)
    }
  }

  const labelItems = []

  landsWithGeometry.forEach((land) => {
    const geometry = getLandGeometry(land)
    const polygons = geometry?.type === 'Polygon'
      ? [geometry.coordinates]
      : geometry?.type === 'MultiPolygon'
        ? geometry.coordinates
        : []

    context.beginPath()
    polygons.forEach((polygon) => {
      polygon.forEach((ring) => {
        ring.forEach((coordinate, coordinateIndex) => {
          const point = project(coordinate)

          if (!point) {
            return
          }

          if (coordinateIndex === 0) {
            context.moveTo(point.x, point.y)
            return
          }

          context.lineTo(point.x, point.y)
        })
        context.closePath()
      })
    })

    context.fillStyle = 'rgba(27, 132, 255, 0.34)'
    context.strokeStyle = '#1b84ff'
    context.lineWidth = 6
    context.fill('evenodd')
    context.stroke()

    const landCoordinates = collectCoordinatePairs(geometry)
    if (landCoordinates.length > 0) {
      const center = getCoordinateBboxCenter(landCoordinates)
      const point = center ? project(center) : null

      if (point) {
        const label = getLandLabel(land)
        const metrics = getOverviewLandLabelMetrics(context, label)

        if (metrics) {
          labelItems.push({label, metrics, point})
        }
      }
    }
  })

  resolveOverviewLabelPositions(labelItems, width, height).forEach((item) => {
    drawOverviewLandLabel(context, item.label, item.point, item.metrics)
  })

  context.strokeStyle = 'rgba(16, 20, 38, 0.16)'
  context.lineWidth = 2
  context.strokeRect(1, 1, width - 2, height - 2)

  return canvas
}

const addPlaceholder = (section, {labels, landKey, subtitle, title}) => {
  section.items.push({
    kind: 'note',
    landKey,
    subtitle: subtitle || labels.unavailable,
    title,
  })
}

const addImageLayerFigures = async ({
  content,
  imageEnhancements = {},
  imageSelections,
  imageTypes,
  labels,
  onProgressStep,
  sections,
  selectedLands,
  product,
}) => {
  for (const type of imageTypes) {
    if (!content[getContentKeyForImageType(type)]) {
      continue
    }

    const section = {
      items: [],
      title: labels.content[getContentKeyForImageType(type)],
    }

    for (const land of selectedLands) {
      const landId = getLandId(land)
      const landKey = String(landId || getLandName(land) || '').trim()
      const selectedDates = imageSelections?.[landId]?.[type] || []

      for (const dateValue of selectedDates) {
        const title = `${labels.images[type]} - ${getLandName(land)}`
        const subtitle = formatInputDate(dateValue)
        const enhanced = !isPlanetHdProduct(product) &&
          ENHANCEABLE_IMAGE_TYPES.includes(type) &&
          Boolean(imageEnhancements[type])

        try {
          if (isPlanetHdProduct(product) && !PLANET_HD_V1_ENABLED) {
            throw new Error('planet-hd-feature-disabled')
          }

          const acquisition = getStoredTechnicalReportAcquisition(
            imageSelections,
            landId,
            dateValue
          )
          if (isPlanetHdProduct(product) && !acquisition?.acquisitionId) {
            throw new Error('planet-hd-acquisition-identity-missing')
          }

          const cancelSource = axios.CancelToken.source()
          const source = normalizeRemoteSensingSource({product, layer: type})
          const res = await requestRemoteSensingLayer({
            acquisition,
            cancelTokenRequest: cancelSource,
            imageType: source.imageType,
            land: {...land, land_id: landId},
            layer: type,
            options: {
              date: dateValue,
              ...(enhanced ? {enhanced: true} : {}),
            },
          })

          if (!res?.ok) {
            addPlaceholder(section, {labels, landKey, subtitle, title})
            continue
          }

          const layerCanvas = await createCanvasFromDrawable(res.data)
          const legendRows = normalizeImageLegend(res.headers).map((row, index) => ({
            color: IMAGE_LEGEND_COLORS[index],
            ha: row.ha,
            label: row.cluster,
            percentage: row.percentage,
          }))
          const figure = drawFigure({
            canvas: layerCanvas,
            labels,
            legendEnhanced: enhanced,
            legendRows,
            legendType: 'image',
            subtitle,
            title,
          })

          section.items.push({
            ...figure,
            landKey,
            subtitle,
            title,
          })
        } catch (error) {
          addPlaceholder(section, {labels, landKey, subtitle, title})
        } finally {
          onProgressStep?.()
        }
      }
    }

    if (section.items.length > 0) {
      sections.push(section)
    }
  }
}

const addAltimetryFigures = async ({labels, onProgressStep, sections, selectedLands}) => {
  const section = {
    items: [],
    title: labels.content.altimetry,
  }

  for (const land of selectedLands) {
    const landKey = String(getLandId(land) || getLandName(land) || '').trim()
    const title = `${labels.content.altimetry} - ${getLandName(land)}`

    try {
      if (!land.polygons) {
        addPlaceholder(section, {labels, landKey, title})
        continue
      }

      const raster = await buildAltimetryRaster(land.polygons)
      const figure = drawFigure({
        altimetryLegend: raster.legend,
        canvas: raster.canvas,
        labels,
        legendType: 'altimetry',
        title,
      })

      section.items.push({
        ...figure,
        landKey,
        title,
      })
    } catch (error) {
      addPlaceholder(section, {labels, landKey, title})
    } finally {
      onProgressStep?.()
    }
  }

  if (section.items.length > 0) {
    sections.push(section)
  }
}

const addSoilMapFigures = async ({labels, onProgressStep, sections, selectedLands}) => {
  const section = {
    items: [],
    title: labels.content.soil_map,
  }

  for (const land of selectedLands) {
    const landId = getLandId(land)
    const landKey = String(landId || getLandName(land) || '').trim()
    const title = `${labels.content.soil_map} - ${getLandName(land)}`

    try {
      const response = await LandService.getSoilMap(landId)
      const soilMap = response?.data
      const canvas = createSoilMapCanvas(soilMap?.featureCollection)

      if (!response?.ok || soilMap?.status !== 'available' || !canvas) {
        addPlaceholder(section, {
          labels: {
            ...labels,
            unavailable: soilMap?.status === 'no_coverage'
              ? labels.soilMap.noCoverage
              : labels.soilMap.sourceUnavailable,
          },
          landKey,
          title,
        })
        continue
      }

      const figure = drawFigure({
        canvas,
        labels,
        legendType: 'soil_map',
        soilMapLegend: getSoilMapLegendModel(soilMap),
        title,
      })

      section.items.push({
        ...figure,
        landKey,
        title,
      })
    } catch (error) {
      addPlaceholder(section, {
        labels: {
          ...labels,
          unavailable: labels.soilMap.sourceUnavailable,
        },
        landKey,
        title,
      })
    } finally {
      onProgressStep?.()
    }
  }

  if (section.items.length > 0) {
    sections.push(section)
  }
}

const addAmbientFigures = async ({
  fetchAmbient,
  labels,
  onProgressStep,
  sectionTitle,
  sections,
  selectedLands,
}) => {
  const section = {
    items: [],
    title: sectionTitle,
  }

  for (const land of selectedLands) {
    const landKey = String(getLandId(land) || getLandName(land) || '').trim()
    const title = `${sectionTitle} - ${getLandName(land)}`

    try {
      const res = await fetchAmbient(getLandId(land))
      const ambient = res?.ok ? res.data : null
      const paintedAmbient = paintAmbientGeojson(ambient)
      const ambientCoordinates = paintedAmbient?.features?.map((feature) => feature.geometry?.coordinates)
      const bbox = getPolygonImageBbox(ambientCoordinates || land.polygons)
      const canvas = createCanvasFromAmbientGeojson(paintedAmbient, bbox)

      if (!ambient || !canvas) {
        addPlaceholder(section, {labels, landKey, title})
        continue
      }

      const legendRows = getAmbientLegendRows(ambient).map((row, index) => ({
        color: row.color,
        ha: row.ha,
        label: `${labels.class} ${row.classNumber || index + 1}`,
        percentage: row.percentage,
      }))
      const figure = drawFigure({
        canvas,
        labels,
        legendRows,
        legendType: 'ambient',
        title,
      })

      section.items.push({
        ...figure,
        landKey,
        title,
      })
    } catch (error) {
      addPlaceholder(section, {labels, landKey, title})
    } finally {
      onProgressStep?.()
    }
  }

  if (section.items.length > 0) {
    sections.push(section)
  }
}

export const buildTechnicalReportVisualSections = async ({
  content,
  currentUser,
  imageEnhancements,
  imageSelections,
  labels,
  onProgress,
  product: requestedProduct,
  selectedLands,
}) => {
  const sections = []
  const product = requestedProduct || getTechnicalReportProduct({imageSelections})
  if (isPlanetHdProduct(product) && !canAccessHistoricalHd(currentUser)) {
    content = {...content, historical_ambient: false}
  }
  const imageTypes = getTechnicalReportImageTypes(product)
  const reportProgress = createTechnicalReportProgressReporter({
    end: 88,
    onProgress,
    start: 4,
    total: countTechnicalReportVisualProgressSteps({
      content,
      imageSelections,
      imageTypes,
      selectedLands,
    }),
  })
  const enrichedLands = await Promise.all(
    selectedLands.map(async (land) => {
      try {
        return await enrichLand(land)
      } finally {
        reportProgress()
      }
    })
  )
  const detailedLands = await enrichLandsWithMapGeojson(
    enrichedLands
  )

  if (content.overview_map) {
    const section = {
      items: [],
      placement: 'cover',
      title: labels.content.overview_map,
    }
    const canvas = await createOverviewCanvas(detailedLands)

    if (canvas) {
      const figure = drawFigure({
        canvas,
        labels,
        title: labels.content.overview_map,
        variant: 'overview',
      })

      section.items.push({
        ...figure,
        title: labels.content.overview_map,
      })
    } else {
      addPlaceholder(section, {labels, title: labels.content.overview_map})
    }

    sections.push(section)
    reportProgress()
  }

  await addImageLayerFigures({
    content,
    imageEnhancements,
    imageSelections,
    imageTypes,
    labels,
    onProgressStep: reportProgress,
    sections,
    selectedLands: detailedLands,
    product,
  })

  if (content.altimetry) {
    await addAltimetryFigures({
      labels,
      onProgressStep: reportProgress,
      sections,
      selectedLands: detailedLands,
    })
  }

  if (content.soil_map) {
    await addSoilMapFigures({
      labels,
      onProgressStep: reportProgress,
      sections,
      selectedLands: detailedLands,
    })
  }

  if (content.historical_ambient) {
    await addAmbientFigures({
      fetchAmbient: isPlanetHdProduct(product) ? getLandAmbientHd : getLandAmbient,
      labels,
      onProgressStep: reportProgress,
      sectionTitle: labels.content.historical_ambient,
      sections,
      selectedLands: detailedLands,
    })
  }

  if (content.image_ambient) {
    await addAmbientFigures({
      fetchAmbient: isPlanetHdProduct(product) ? getLandImageAmbientHd : getLandImageAmbient,
      labels,
      onProgressStep: reportProgress,
      sectionTitle: labels.content.image_ambient,
      sections,
      selectedLands: detailedLands,
    })
  }

  return sections
}
